import React from "react";
import { useTranslation } from "react-i18next";
import { Link, Typography } from "@mui/material";
import { toExternalHref } from "utils/url";
import { EMPTY } from "components/common/detailRows";
import {
  SettingRow,
  SettingRows,
} from "components/platform/accounts/SettingsCard";

type SavedAccount = Partial<
  Record<
    | "businessLegalName"
    | "businessDBA"
    | "businessCardName"
    | "businessEmail"
    | "phoneNumber"
    | "websiteURL"
    | "description",
    string | null
  >
>;

/** The account's saved business details, read back one row each, on its Account details tab. */
const AccountHolderDetails: React.FC<{ account: SavedAccount }> = ({
  account,
}) => {
  const { t } = useTranslation();

  const rows: { label: string; value: React.ReactNode }[] = [
    { label: t("Legal Name"), value: account.businessLegalName },
    { label: t("Doing Business As (DBA)"), value: account.businessDBA },
    { label: t("Business Card Name"), value: account.businessCardName },
    { label: t("Email"), value: account.businessEmail },
    { label: t("Phone"), value: account.phoneNumber },
    {
      label: t("Website"),
      value: account.websiteURL && (
        <Link
          href={toExternalHref(account.websiteURL)}
          target="_blank"
          rel="noopener noreferrer"
          underline="hover"
        >
          {account.websiteURL}
        </Link>
      ),
    },
    { label: t("Description"), value: account.description },
  ];

  return (
    <SettingRows>
      {rows.map(({ label, value }, index) => (
        <SettingRow
          key={label}
          title={label}
          divider={index < rows.length - 1}
          control={() => (
            // A long value wraps on its own side rather than pushing the label away.
            <Typography
              variant="body1"
              sx={{ textAlign: "right", overflowWrap: "anywhere" }}
            >
              {value || EMPTY}
            </Typography>
          )}
        />
      ))}
    </SettingRows>
  );
};

export default AccountHolderDetails;
